import React from 'react';
import { Heart, HandHeart } from 'lucide-react';

const LeafSVG = ({ className = '', style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none">
    <path d="M12 2C8 4 4 8 4 14c0 4 3 7 8 7 0-6 0-10 0-19z" fill="#8fa05a" opacity="0.85" />
    <path d="M12 5v15" stroke="#5a6a3a" strokeWidth="0.8" />
  </svg>
);

const Hero = () => {
  return (
    <section id="home" className="relative pt-32 md:pt-36 pb-16 md:pb-24 paper-bg overflow-hidden">
      {/* Floating leaves */}
      <LeafSVG className="absolute top-28 left-6 w-7 h-7 float-leaf hidden md:block" />
      <LeafSVG className="absolute top-1/2 right-4 w-9 h-9 float-leaf hidden md:block" style={{ animationDelay: '1.5s' }} />
      <LeafSVG className="absolute bottom-10 left-1/3 w-6 h-6 float-leaf hidden md:block" style={{ animationDelay: '3s' }} />

      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left content */}
        <div className="relative z-10">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-sm tracking-wide text-[#5a6a4a] mb-6">
              <span className="h-px w-8 bg-[#7a8a4c]" />
              Compassionate counseling
            </span>
            <h1 className="font-serif text-[44px] sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.05] text-[#2c3f25] tracking-tight">
              You don’t have <br /> to face it <em className="not-italic">alone.</em>
            </h1>
            <p className="mt-7 text-base md:text-lg text-[#3d4a36] max-w-xl">
              Support for <span className="font-medium">Anxiety</span>. Relief from <span className="font-medium">Depression</span>.
            </p>
            <p className="font-script text-3xl md:text-4xl text-[#3a5232] mt-2 flex items-center gap-2">
              Hope for a better tomorrow.
              <Heart className="text-[#8fa05a] fill-[#cfe0a8]" size={22} />
            </p>

            <p className="mt-7 text-[15px] md:text-base text-[#4a5640] max-w-lg leading-relaxed">
              Compassionate and confidential sessions to help you heal, grow and live a more peaceful and meaningful life.
            </p>

            {/* Highlight badge */}
            <div className="mt-9 flex items-start gap-4 max-w-xl">
              <div className="shrink-0 w-14 h-14 rounded-full bg-[#3a5232] flex items-center justify-center shadow-md gentle-scale">
                <HandHeart className="text-[#f0e9d2]" size={24} />
              </div>
              <div className="bg-[#dbe2c2] rounded-md px-5 py-3 text-[#2c3f25]">
                <p className="text-[15px] leading-snug">
                  Taking sessions <em className="font-script not-italic text-xl text-[#3a5232]">to the people</em>
                  <br />
                  who are suffering from
                </p>
                <p className="font-serif text-xl md:text-2xl font-semibold mt-1">Anxiety & Depression.</p>
              </div>
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#contact" className="btn-primary px-7 py-3.5 rounded-full text-sm font-medium">
                Take the first step
              </a>
              <a href="#services" className="btn-outline px-7 py-3.5 rounded-full text-sm font-medium">
                Explore services
              </a>
            </div>
          </div>
        </div>

        {/* Right image */}
        <div className="relative animate-fade-in">
          <div className="absolute -top-6 right-2 md:right-10 text-right z-10">
            <p className="font-script text-2xl md:text-3xl text-[#3a5232] leading-tight">You matter.</p>
            <p className="font-script text-2xl md:text-3xl text-[#3a5232] leading-tight script-underline">
              Your mental health matters.
            </p>
          </div>

          <div className="relative rounded-[28px] overflow-hidden shadow-xl hero-vignette aspect-[4/5] md:aspect-[5/6] lg:aspect-[4/5]">
            <img
              src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtZWRpdGF0aW9uJTIwc3VucmlzZXxlbnwwfHx8fDE3ODEyNTU2NjV8MA&ixlib=rb-4.1.0&q=85"
              alt="Person sitting peacefully in nature at sunrise"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Decorative leaf in foreground */}
          <LeafSVG className="absolute -bottom-4 -right-2 w-24 h-24 md:w-32 md:h-32 float-leaf" style={{ animationDelay: '0.8s' }} />
          <LeafSVG className="absolute -top-3 left-4 w-10 h-10 md:hidden" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
